/**
 * Scoring ponderado raíz × miotoma para radiculopatía.
 * Un músculo aporta evidencia distribuida entre varias raíces según rootContributions.
 */

import {
  computeMuscleRadicularEvidence,
  evaluateLMNSigns,
  getMyotomeMappingForMuscle,
  getRootContributionsForMapping,
  LMNMuscleEvaluationInput,
  RADICULAR_ROOT_THRESHOLDS,
  ROOT_ORDER,
} from '../data/emgClinicalCriteria';
import type { ClinicalReport } from './jsonReportGenerator';

export interface RootMatrixCell {
  muscleName: string;
  peripheralNerve: string;
  roots: string[];
  primaryRoots: string[];
  isParaspinal: boolean;
  hasActiveDenervation: boolean;
  hasChronicNeurogenicChanges: boolean;
  rationale: string;
  evidenceStrength: number;
  segment?: string;
}

export interface RootMatrixRow {
  root: string;
  cells: RootMatrixCell[];
  peripheralNerves: string[];
  paraspinalMuscles: string[];
  score: number;
  tier: RootConfidenceTier;
}

export type RootConfidenceTier = 'confirmed' | 'probable' | 'possible' | 'none';

export interface WeightedRadiculopathyMatrix {
  confirmedRows: RootMatrixRow[];
  probableRows: RootMatrixRow[];
  candidateRows: RootMatrixRow[];
  unmappedAbnormalMuscles: string[];
  isMultilevel: boolean;
  affectedRoots: string[];
  dominantRoot: string | null;
}

const CONTIGUITY_BONUS = 0.12;

export function sortRootsAnatomically(roots: string[]): string[] {
  return [...roots].sort((a, b) => {
    const indexA = ROOT_ORDER.indexOf(a.toUpperCase() as (typeof ROOT_ORDER)[number]);
    const indexB = ROOT_ORDER.indexOf(b.toUpperCase() as (typeof ROOT_ORDER)[number]);
    if (indexA < 0 && indexB < 0) return a.localeCompare(b);
    if (indexA < 0) return 1;
    if (indexB < 0) return -1;
    return indexA - indexB;
  });
}

function isParaspinalMuscleName(name: string): boolean {
  const n = name.toLowerCase();
  return n.includes('paravertebral') || n.includes('paraspinal') || n.includes('paraespinal') || n.includes('multifidus');
}

function collectRadiculopathyMuscles(report: ClinicalReport): Array<LMNMuscleEvaluationInput & { recruitmentPattern?: string }> {
  const records =
    report.electrophysiologicalData.emg?.records?.map(record => ({
      muscleOrNerveName: record.muscleOrNerveName,
      spontaneousActivity: record.spontaneousActivity,
      motorUnitPotentials: record.motorUnitPotentials,
      status: record.insertionalActivity || undefined,
      recruitmentPattern: record.recruitmentPattern,
    })) || [];

  const muscles =
    report.electrophysiologicalData.emg?.muscles?.map(muscle => ({
      muscleName: muscle.muscleName,
      spontaneousActivitySummary: muscle.spontaneousActivitySummary,
      mupAmplitude: muscle.mupAmplitude,
      mupDuration: muscle.mupDuration,
      mupPolyphasia: muscle.mupPolyphasia,
      recruitmentPattern: muscle.recruitmentPattern,
    })) || [];

  return records.length > 0 ? records : muscles;
}

function classifyRootTier(
  score: number,
  limbNerves: string[],
  paraspinalMuscles: string[],
  limbMuscleCount: number
): RootConfidenceTier {
  const hasParaspinal = paraspinalMuscles.length > 0;
  const multiNerve = limbNerves.length >= 2 && limbMuscleCount >= 2;

  if (score >= RADICULAR_ROOT_THRESHOLDS.confirmed && (multiNerve || (hasParaspinal && limbMuscleCount >= 1))) {
    return 'confirmed';
  }
  if (score >= RADICULAR_ROOT_THRESHOLDS.probable && (hasParaspinal || limbMuscleCount >= 1)) {
    return 'probable';
  }
  if (score >= RADICULAR_ROOT_THRESHOLDS.possible) {
    return 'possible';
  }
  return 'none';
}

function areContiguousRoots(roots: string[]): boolean {
  if (roots.length < 2) return false;
  const indices = roots
    .map(root => ROOT_ORDER.indexOf(root as typeof ROOT_ORDER[number]))
    .filter(index => index >= 0)
    .sort((a, b) => a - b);
  if (indices.length < 2) return false;
  for (let i = 1; i < indices.length; i++) {
    if (indices[i] - indices[i - 1] !== 1) return false;
  }
  return true;
}

export function buildWeightedRadiculopathyMatrix(report: ClinicalReport): WeightedRadiculopathyMatrix {
  const rows = new Map<string, RootMatrixCell[]>();
  const rootScores = new Map<string, number>();
  const unmappedAbnormalMuscles: string[] = [];

  for (const muscle of collectRadiculopathyMuscles(report)) {
    const lmn = evaluateLMNSigns(muscle);
    const evidenceStrength = computeMuscleRadicularEvidence(lmn, muscle.recruitmentPattern);
    if (evidenceStrength <= 0) continue;

    const mapping = getMyotomeMappingForMuscle(lmn.muscleName);
    if (!mapping || mapping.roots.length === 0) {
      unmappedAbnormalMuscles.push(lmn.muscleName);
      continue;
    }

    const cell: RootMatrixCell = {
      muscleName: lmn.muscleName,
      peripheralNerve: mapping.peripheralNerve,
      roots: mapping.roots,
      primaryRoots: mapping.primaryRoots,
      isParaspinal: mapping.isParaspinal || isParaspinalMuscleName(lmn.muscleName),
      hasActiveDenervation: lmn.activeDenervation,
      hasChronicNeurogenicChanges: Object.values(lmn.chronicMuapSigns).some(Boolean),
      rationale: lmn.rationale,
      evidenceStrength,
      segment: mapping.segment,
    };

    for (const contribution of getRootContributionsForMapping(mapping)) {
      const weighted = evidenceStrength * contribution.weight;
      rootScores.set(contribution.root, (rootScores.get(contribution.root) || 0) + weighted);
      rows.set(contribution.root, [...(rows.get(contribution.root) || []), cell]);
    }
  }

  let candidateRows: RootMatrixRow[] = [...rows.entries()]
    .map(([root, cells]) => {
      const uniqueCells = cells.filter(
        (cell, index, list) => list.findIndex(item => item.muscleName === cell.muscleName) === index
      );
      const limbCells = uniqueCells.filter(cell => !cell.isParaspinal);
      const paraspinalMuscles = uniqueCells.filter(cell => cell.isParaspinal).map(cell => cell.muscleName);
      const peripheralNerves = Array.from(new Set(limbCells.map(cell => cell.peripheralNerve)));
      const score = rootScores.get(root) || 0;
      const tier = classifyRootTier(score, peripheralNerves, paraspinalMuscles, limbCells.length);
      return {
        root,
        cells: uniqueCells,
        peripheralNerves,
        paraspinalMuscles,
        score,
        tier,
      };
    })
    .filter(row => row.score > 0)
    .sort((a, b) => b.score - a.score);

  // Bonus por raíces contiguas con compromiso probable/confirmado
  const significantRoots = candidateRows.filter(row => row.tier === 'confirmed' || row.tier === 'probable');
  if (significantRoots.length >= 2 && areContiguousRoots(significantRoots.map(row => row.root))) {
    const bonus = CONTIGUITY_BONUS * Math.min(significantRoots.length - 1, 2);
    candidateRows = candidateRows.map(row => {
      if (row.tier === 'none') return row;
      const nextScore = row.score + bonus;
      return {
        ...row,
        score: nextScore,
        tier: classifyRootTier(nextScore, row.peripheralNerves, row.paraspinalMuscles, row.cells.filter(cell => !cell.isParaspinal).length),
      };
    });
  }

  const confirmedRows = candidateRows.filter(row => row.tier === 'confirmed');
  const probableRows = candidateRows.filter(row => row.tier === 'probable');
  const significant = [...confirmedRows, ...probableRows];
  const affectedRoots = sortRootsAnatomically(significant.map(row => row.root));
  const isMultilevel = significant.length >= 2;
  const dominantRoot = candidateRows[0]?.root || null;

  return {
    confirmedRows,
    probableRows,
    candidateRows,
    unmappedAbnormalMuscles,
    isMultilevel,
    affectedRoots,
    dominantRoot,
  };
}
